import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import SuperAdminLayout from '../components/admin/SuperAdminLayout';
import { adminService } from '../services/api';

const SuperAdminDashboard = () => {
    const [stats, setStats] = useState({
        total_klinik: 0,
        total_user: 0,
        active_subscriptions: 0,
        total_pendapatan: 0
    });

    const [clinicData, setClinicData] = useState([]);
    const [userData, setUserData] = useState([]);
    const [clinicSort, setClinicSort] = useState({ key: 'name', direction: 'asc' });
    const [userSort, setUserSort] = useState({ key: 'name', direction: 'asc' });
    const [clinicPage, setClinicPage] = useState(1);
    const [userPage, setUserPage] = useState(1);
    const perPage = 10;

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await adminService.getDashboardStats();
                if (response.data && response.data.success) {
                    setStats(response.data.data);

                    // Transform subscriptions for klinik table
                    if (response.data.data.subscriptions) {
                        const transformedClinics = response.data.data.subscriptions.map(sub => {
                            let statusText = 'Aktif';
                            let statusColor = 'bg-[#0DBC94]';
                            
                            if (sub.status === 'pending') {
                                statusText = 'Trial';
                                statusColor = 'bg-[#FFB536]';
                            } else if (sub.status === 'failed' || sub.status === 'expired') {
                                statusText = 'Kadaluarsa';
                                statusColor = 'bg-[#F64E60]';
                            }

                            return {
                                id: sub.order_id,
                                name: sub.clinic_name,
                                avatarColor: 'bg-gray-100',
                                address: sub.address || '-',
                                date: sub.created_at ? sub.created_at.split('T')[0] : '-',
                                time: '',
                                package: sub.plan || '-',
                                price: new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(sub.amount || 0),
                                status: statusText,
                                statusColor: statusColor,
                                displayDate: sub.created_at ? new Date(sub.created_at).toLocaleString('id-ID', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute:'2-digit'}).replace(',', ' -') + ' PM' : '-'  // Simplified mock format
                            };
                        });
                        setClinicData(transformedClinics);
                    }

                    // Transform users for users table
                    if (response.data.data.users) {
                        const transformedUsers = response.data.data.users.map(u => ({
                            id: u.id,
                            name: u.name,
                            email: u.email,
                            phone: '-', // No phone in user table by default
                            role: u.role || 'user',
                            status: 'Aktif',
                            statusColor: 'bg-[#0DBC94]'
                        }));
                        setUserData(transformedUsers);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch dashboard stats", error);
            }
        };
        fetchStats();
    }, []);

    // Filter Logic for Klinik
    const [selectedDate, setSelectedDate] = useState('');
    const [filterStatus, setFilterStatus] = useState('Semua');

    const filteredData = clinicData.filter(item => {
        const matchDate = selectedDate ? item.date === selectedDate : true;
        const matchStatus = filterStatus === 'Semua' ? true : item.status === filterStatus;
        return matchDate && matchStatus;
    });
    const sortedClinicData = [...filteredData].sort((a, b) => {
        const direction = clinicSort.direction === 'asc' ? 1 : -1;
        return String(a?.[clinicSort.key] ?? '').localeCompare(String(b?.[clinicSort.key] ?? ''), 'id', { numeric: true }) * direction;
    });
    const clinicTotalPages = Math.max(1, Math.ceil(sortedClinicData.length / perPage));
    const clinicRows = sortedClinicData.slice((clinicPage - 1) * perPage, clinicPage * perPage);
    const sortedUserData = [...userData].sort((a, b) => {
        const direction = userSort.direction === 'asc' ? 1 : -1;
        return String(a?.[userSort.key] ?? '').localeCompare(String(b?.[userSort.key] ?? ''), 'id', { numeric: true }) * direction;
    });
    const userTotalPages = Math.max(1, Math.ceil(sortedUserData.length / perPage));
    const userRows = sortedUserData.slice((userPage - 1) * perPage, userPage * perPage);

    // CRUD Logic for Clinic
    const [isEditClinicOpen, setIsEditClinicOpen] = useState(false);
    const [currentClinic, setCurrentClinic] = useState(null);
    const [clinicFormData, setClinicFormData] = useState({ status: 'paid' });

    const handleEditClinicClick = (clinic) => {
        setCurrentClinic(clinic);
        let rawStatus = 'paid';
        if (clinic.status === 'Trial') rawStatus = 'pending';
        if (clinic.status === 'Kadaluarsa') rawStatus = 'failed';
        setClinicFormData({ status: rawStatus });
        setIsEditClinicOpen(true);
    };

    const handleUpdateClinic = async (e) => {
        e.preventDefault();
        try {
            await adminService.updateSubscription(currentClinic.id, clinicFormData);
            
            // Re-fetch or manually update local state
            const updatedClinics = clinicData.map(clinic => {
                if (clinic.id === currentClinic.id) {
                    let statusText = 'Aktif';
                    let statusColor = 'bg-[#0DBC94]';
                    if (clinicFormData.status === 'pending') {
                        statusText = 'Trial';
                        statusColor = 'bg-[#FFB536]';
                    } else if (clinicFormData.status === 'failed' || clinicFormData.status === 'expired') {
                        statusText = 'Kadaluarsa';
                        statusColor = 'bg-[#F64E60]';
                    }
                    return { ...clinic, status: statusText, statusColor: statusColor };
                }
                return clinic;
            });
            setClinicData(updatedClinics);
            setIsEditClinicOpen(false);
        } catch (error) {
            console.error("Failed to update clinic status", error);
        }
    };

    // CRUD Logic for User
    const [isEditUserOpen, setIsEditUserOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', role: 'Admin', status: 'Aktif' });


    const handleEditClick = (user) => {
        setCurrentUser(user);
        setFormData({ name: user.name, email: user.email, phone: user.phone, role: user.role, status: user.status });
        setIsEditUserOpen(true);
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        try {
            await adminService.updateUser(currentUser.id, formData);
            const updatedUsers = userData.map(user =>
                user.id === currentUser.id
                    ? { ...user, ...formData, statusColor: formData.status === 'Aktif' ? 'bg-[#0DBC94]' : 'bg-[#F64E60]' }
                    : user
            );
            setUserData(updatedUsers);
            setIsEditUserOpen(false);
        } catch (error) {
            console.error("Failed to update user", error);
        }
    };

    const handleDeleteClick = (user) => {
        setCurrentUser(user);
        setIsDeleteConfirmOpen(true);
    };

    const handleDeleteUser = async () => {
        try {
            await adminService.deleteUser(currentUser.id);
            const remainingUsers = userData.filter(user => user.id !== currentUser.id);
            setUserData(remainingUsers);
            setIsDeleteConfirmOpen(false);
        } catch (error) {
            console.error("Failed to delete user", error);
        }
    };

    return (
        <SuperAdminLayout activePage="dashboard" title="Dashboard">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                {/* Stat Card 1 - Total Klinik */}
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-50 flex items-start justify-between relative overflow-hidden group hover:shadow-md transition-all">
                    <div className="space-y-4 relative z-10 w-full overflow-hidden">
                        <p className="text-gray-400 text-sm font-medium">Total Klinik</p>
                        <h2 className="text-4xl font-black text-gray-800 tracking-tight">{stats.total_klinik}</h2>
                        <div className="flex items-center gap-1 text-[#06B6D4] text-xs font-bold">
                            <Icon icon="mdi:trending-up" className="text-base" />
                            <span>{stats.total_klinik} Total Klinik</span>
                        </div>
                    </div>
                    <div className="w-14 h-14 bg-[#E0F7FA] rounded-2xl flex items-center justify-center text-[#06B6D4] group-hover:scale-110 transition-transform shrink-0 ml-2">
                        <Icon icon="material-symbols:box-outline" className="text-3xl" />
                    </div>
                </div>

                {/* Stat Card 2 - Total User */}
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-50 flex items-start justify-between relative overflow-hidden group hover:shadow-md transition-all">
                    <div className="space-y-4 relative z-10 w-full overflow-hidden">
                        <p className="text-gray-400 text-sm font-medium">Total Pengguna Terdaftar</p>
                        <h2 className="text-4xl font-black text-gray-800 tracking-tight">{stats.total_user}</h2>
                        <div className="flex items-center gap-1 text-[#06B6D4] text-xs font-bold">
                            <Icon icon="mdi:trending-up" className="text-base" />
                            <span>{stats.total_user} pengguna</span>
                        </div>
                    </div>
                    <div className="w-14 h-14 bg-[#E0F7FA] rounded-2xl flex items-center justify-center text-[#06B6D4] group-hover:scale-110 transition-transform shrink-0 ml-2">
                        <Icon icon="material-symbols:group-outline" className="text-3xl" />
                    </div>
                </div>

                {/* Stat Card 3 - Subscription */}
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-50 flex items-start justify-between relative overflow-hidden group hover:shadow-md transition-all">
                    <div className="space-y-4 relative z-10 w-full overflow-hidden">
                        <p className="text-gray-400 text-sm font-medium">Subscription Aktif</p>
                        <h2 className="text-4xl font-black text-gray-800 tracking-tight">{stats.active_subscriptions}</h2>
                        <div className="flex items-center gap-1 text-[#06B6D4] text-xs font-bold">
                            <Icon icon="mdi:trending-up" className="text-base" />
                            <span>{stats.active_subscriptions} Aktif</span>
                        </div>
                    </div>
                    <div className="w-14 h-14 bg-[#E0F7FA] rounded-2xl flex items-center justify-center text-[#06B6D4] group-hover:scale-110 transition-transform shrink-0 ml-2">
                        <Icon icon="material-symbols:monitoring" className="text-3xl" />
                    </div>
                </div>

                {/* Stat Card 4 - Total Pendapatan */}
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-50 flex items-start justify-between relative overflow-hidden group hover:shadow-md transition-all">
                    <div className="space-y-4 relative z-10 w-full overflow-hidden">
                        <p className="text-gray-400 text-sm font-medium truncate w-full" title="Total Pendapatan">Total Pendapatan</p>
                        <h2 className="text-2xl font-black text-gray-800 tracking-tight overflow-hidden text-ellipsis w-full" title={new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(stats.total_pendapatan || 0)}>
                            {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(stats.total_pendapatan || 0)}
                        </h2>
                        <div className="flex items-center gap-1 text-[#0DBC94] text-xs font-bold">
                            <Icon icon="mdi:currency-usd" className="text-base" />
                            <span>Uang Masuk</span>
                        </div>
                    </div>
                    <div className="w-14 h-14 bg-[#E8F8EA] rounded-2xl flex items-center justify-center text-[#0DBC94] group-hover:scale-110 transition-transform shrink-0 ml-2">
                        <Icon icon="material-symbols:account-balance-wallet-outline" className="text-3xl" />
                    </div>
                </div>
            </div>

            {/* Total Klinik Table */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-50 mb-8 p-8">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <h3 className="text-xl font-bold text-gray-800 leading-none">Total Klinik</h3>

                    {/* Filters */}
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600 focus:outline-none focus:border-[#0DBC94] transition-colors"
                        />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600 focus:outline-none focus:border-[#0DBC94] transition-colors cursor-pointer"
                        >
                            <option value="Semua">Semua Status</option>
                            <option value="Aktif">Aktif</option>
                            <option value="Trial">Trial</option>
                            <option value="Kadaluarsa">Kadaluarsa</option>
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50">
                                <th onClick={() => setClinicSort((p) => ({ key: 'name', direction: p.key === 'name' && p.direction === 'asc' ? 'desc' : 'asc' }))} className="cursor-pointer px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider rounded-l-xl">Nama Klinik</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Alamat</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Tanggal - Waktu</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Paket</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Harga</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider rounded-r-xl">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {clinicRows.length > 0 ? (
                                clinicRows.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50 transition-colors group">
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 ${item.avatarColor} rounded-full flex-shrink-0`}></div>
                                                <span className="font-bold text-sm text-gray-700">{item.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-sm font-medium text-gray-500">{item.address}</td>
                                        <td className="px-6 py-5 text-sm font-medium text-gray-500">{item.displayDate}</td>
                                        <td className="px-6 py-5 text-sm font-medium text-gray-500">{item.package}</td>
                                        <td className="px-6 py-5 text-sm font-medium text-gray-500">{item.price}</td>
                                        <td className="px-6 py-5">
                                            <div className={`flex items-center justify-center w-[110px] h-8 ${item.statusColor} text-white text-[10px] font-black rounded-lg`}>
                                                {item.status}
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <button
                                                onClick={() => handleEditClinicClick(item)}
                                                className="p-2 text-gray-400 hover:text-[#06B6D4] hover:bg-[#E0F7FA] rounded-lg transition-colors"
                                                title="Edit Status"
                                            >
                                                <Icon icon="material-symbols:edit-outline" className="text-xl" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500 text-sm">
                                        Tidak ada data yang ditemukan
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                    <button disabled={clinicPage === 1} onClick={() => setClinicPage((p) => Math.max(1, p - 1))} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
                    <span className="text-sm text-gray-500">{clinicPage}/{clinicTotalPages}</span>
                    <button disabled={clinicPage === clinicTotalPages} onClick={() => setClinicPage((p) => Math.min(clinicTotalPages, p + 1))} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                </div>
            </div>

            {/* Total User Table */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-50 p-8">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-800 leading-none">Total User</h3>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50">
                                <th onClick={() => setUserSort((p) => ({ key: 'name', direction: p.key === 'name' && p.direction === 'asc' ? 'desc' : 'asc' }))} className="cursor-pointer px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider rounded-l-xl">Nama</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Email</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">No. Telepon</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Role User</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider rounded-r-xl">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {userRows.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50 transition-colors group">
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gray-100 rounded-full flex-shrink-0"></div>
                                            <span className="font-bold text-sm text-gray-700">{user.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-sm font-medium text-gray-500">{user.email}</td>
                                    <td className="px-6 py-5 text-sm font-medium text-gray-500">{user.phone}</td>
                                    <td className="px-6 py-5 text-sm font-medium text-gray-500">{user.role}</td>
                                    <td className="px-6 py-5">
                                        <div className={`flex items-center justify-center w-[110px] h-8 ${user.statusColor} text-white text-[10px] font-black rounded-lg`}>
                                            {user.status}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleEditClick(user)}
                                                className="p-2 text-gray-400 hover:text-[#06B6D4] hover:bg-[#E0F7FA] rounded-lg transition-colors"
                                            >
                                                <Icon icon="material-symbols:edit-outline" className="text-xl" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteClick(user)}
                                                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            >
                                                <Icon icon="material-symbols:delete-outline" className="text-xl" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                    <button disabled={userPage === 1} onClick={() => setUserPage((p) => Math.max(1, p - 1))} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
                    <span className="text-sm text-gray-500">{userPage}/{userTotalPages}</span>
                    <button disabled={userPage === userTotalPages} onClick={() => setUserPage((p) => Math.min(userTotalPages, p + 1))} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                </div>
            </div>


            {/* Edit User Modal */}
            {isEditUserOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl animate-fade-in">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-800">Edit User</h3>
                            <button onClick={() => setIsEditUserOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <Icon icon="material-symbols:close" className="text-2xl" />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateUser} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Nama</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#06B6D4] focus:ring-1 focus:ring-[#06B6D4] transition-all"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#06B6D4] focus:ring-1 focus:ring-[#06B6D4] transition-all"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">No. Telepon</label>
                                <input
                                    type="text"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#06B6D4] focus:ring-1 focus:ring-[#06B6D4] transition-all"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#06B6D4] focus:ring-1 focus:ring-[#06B6D4] transition-all"
                                >
                                    <option value="Aktif">Aktif</option>
                                    <option value="Non-Aktif">Non-Aktif</option>
                                </select>
                            </div>
                            <button type="submit" className="w-full py-3 bg-[#06B6D4] text-white font-bold rounded-xl hover:bg-[#0891b2] transition-colors mt-4">
                                Simpan Perubahan
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteConfirmOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl animate-fade-in text-center">
                        <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Icon icon="material-symbols:warning-outline" className="text-3xl" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Hapus User?</h3>
                        <p className="text-gray-500 mb-8">Apakah anda yakin ingin menghapus user <strong>{currentUser?.name}</strong>? Tindakan ini tidak dapat dibatalkan.</p>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setIsDeleteConfirmOpen(false)}
                                className="flex-1 py-3 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-colors"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleDeleteUser}
                                className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-colors"
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Edit Clinic Status Modal */}
            {isEditClinicOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl animate-fade-in">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-800">Edit Status Klinik</h3>
                            <button onClick={() => setIsEditClinicOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <Icon icon="material-symbols:close" className="text-2xl" />
                            </button>
                        </div>
                        <div className="mb-4">
                            <p className="text-sm font-medium text-gray-500">Nama Klinik: <span className="font-bold text-gray-800">{currentClinic?.name}</span></p>
                        </div>
                        <form onSubmit={handleUpdateClinic} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
                                <select
                                    value={clinicFormData.status}
                                    onChange={(e) => setClinicFormData({ ...clinicFormData, status: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#0DBC94] focus:ring-1 focus:ring-[#0DBC94] transition-all"
                                >
                                    <option value="paid">Aktif</option>
                                    <option value="pending">Trial</option>
                                    <option value="failed">Kadaluarsa</option>
                                </select>
                            </div>
                            <button type="submit" className="w-full py-3 bg-[#0DBC94] text-white font-bold rounded-xl hover:bg-[#0ba783] transition-colors mt-4">
                                Simpan Perubahan
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </SuperAdminLayout>
    );
};

export default SuperAdminDashboard;
